import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  MapPin,
  ExternalLink,
  Users,
  Clock,
  Tag,
  DollarSign,
  Ticket,
  UserCheck,
  Globe,
  Share2,
  Copy,
  AlertCircle,
} from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { useAnalytics } from '../hooks/useAnalytics';
import EventFinderAPI from '../services/api';
import { EventInfo, RelatedEventItem, RelatedEventsResponse } from '../types/api';
import RelatedEventsSection from '../components/events/RelatedEventsSection';
import {
  DetailSection as Section,
  DetailSectionTitle as SectionTitle,
} from '../components/events/DetailSection';
import SEO, { createEventStructuredData, createBreadcrumbStructuredData } from '../components/SEO';
import {
  getVenueName,
  getVenueStreetAddress,
  getVenueFormattedAddress,
  getVenueGeocodeStatus,
  getVenueAddressNotice,
  hasVenueCoordinates,
  isLocationRedundantWithVenue,
  getLocationLabel,
  getEventCoordinates,
  getCoordinatePrecisionLabel,
  formatPrice,
  formatCapacity,
  formatAudienceLevel,
  formatDeadline,
  formatSpeaker,
  cleanStringArray,
} from '../utils/eventFields';
import { formatEventLocalDateTime, formatEventLocalDuration } from '../utils/eventDateTime';
import { getEventDateTimeDisplay } from '../utils/eventPresentation';
import {
  BadgeRow,
  FormatBadge,
  TimeOfDayBadge,
  UniversityHostedBadge,
  VibeBadges,
} from '../components/events/EventBadges';
import EventPrimaryHostBadge from '../components/events/EventPrimaryHostBadge';
import EventInvolvementSection from '../components/events/EventInvolvementSection';
import EventSourcesDisclosure from '../components/events/EventSourcesDisclosure';
import { downloadEventIcs, shareEvent } from '../utils/eventCalendar';
import { relationKey } from '../utils/relatedEvents';
import { buildEventPath } from '../utils/slug';
import { toAbsoluteUrl } from '../utils/externalUrl';
import { canonicalUrl } from '../utils/seo';
import type { Event } from '../types/api';
import { copyEventSummary } from '../utils/eventClipboard';
import config from '../config';
import RememberButton from '../components/events/RememberButton';
import { useTranslations } from '../locales/useI18n';

const EventDetailsPage = styled.div`
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const Container = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: ${spacing[8]} ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[8]} ${spacing[6]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    padding: ${spacing[8]} ${spacing[8]};
  }
`;

const NavigationRow = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[6]};
`;

const navigationActionStyles = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  min-height: 44px;
  padding: 0 ${spacing[2]};
  background: none;
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  color: ${colors.text.secondary};
  cursor: pointer;
  font-family: inherit;
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.tight};
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.background.secondary};
    color: ${colors.text.primary};
  }
`;

const NavigationButton = styled.button`
  ${navigationActionStyles}
`;

const NavigationAnchor = styled.a`
  ${navigationActionStyles}
`;

const NavigationLink = styled(Link)`
  ${navigationActionStyles}
`;

const AROUND_THE_SAME_TIME_SECTION_ID = 'around-the-same-time-events';
const SIMILAR_EVENTS_SECTION_ID = 'similar-events';

const EventHeader = styled.div`
  background: ${colors.gradients.hero};
  color: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[8]};
  margin-bottom: ${spacing[8]};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[6]};
  }
`;

const EventTitle = styled.h1`
  font-size: ${typography.fontSizes['3xl']};
  font-weight: ${typography.fontWeights.bold};
  margin-bottom: ${spacing[4]};
  position: relative;
  z-index: 1;
  color: ${colors.white};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  position: relative;
  z-index: 1;
  
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    gap: ${spacing[2]};
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.base};
  opacity: 0.9;
`;

const EventActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  position: relative;
  z-index: 1;
  
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[6]};
  background: ${colors.white};
  color: ${colors.primary[600]};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
  justify-content: center;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.primary[50]};
    transform: translateY(-1px);
  }
  
  &.secondary {
    background: transparent;
    color: ${colors.white};
    border: 2px solid ${colors.white};
    
    &:hover {
      background: ${colors.white};
      color: ${colors.primary[600]};
    }
  }
`;

const EventContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${spacing[8]};
  
  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${spacing[6]};
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div``;

const Description = styled.div`
  font-size: ${typography.fontSizes.base};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
  margin-bottom: ${spacing[4]};
`;

const SummaryAttribution = styled.a`
  display: inline-flex;
  margin-top: ${spacing[3]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const DetailsList = styled.dl`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${spacing[3]} ${spacing[4]};
  
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[2]};
  }
`;

const DetailTerm = styled.dt`
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const DetailDefinition = styled.dd`
  color: ${colors.text.secondary};
  margin: 0;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const TagPill = styled.span<{ $tone?: 'price' | 'audience' | 'topic' | 'industry' | 'role' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  background: ${(props) =>
    props.$tone === 'price'
      ? colors.success[50]
      : props.$tone === 'audience'
      ? colors.primary[50]
      : props.$tone === 'industry'
      ? colors.warning[50]
      : colors.background.secondary};
  color: ${(props) =>
    props.$tone === 'price'
      ? colors.success[700]
      : props.$tone === 'audience'
      ? colors.primary[700]
      : colors.text.secondary};
  border: 1px solid ${(props) =>
    props.$tone === 'price'
      ? colors.success[100]
      : props.$tone === 'audience'
      ? colors.primary[100]
      : colors.border.light};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const SpeakerCard = styled.div`
  background: ${colors.background.secondary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]} ${spacing[4]};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SpeakerName = styled.div`
  font-weight: ${typography.fontWeights.semibold};
`;

const SpeakerMeta = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  margin-top: 2px;
`;

const KeynoteBadge = styled.span`
  display: inline-block;
  margin-left: ${spacing[2]};
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: 10px;
  font-weight: ${typography.fontWeights.semibold};
  background: ${colors.primary[100]};
  color: ${colors.primary[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Subtle = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  margin-top: ${spacing[1]};
`;

const RegisterButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[5]};
  background: ${colors.primary[500]};
  color: ${colors.white};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.primary[600]};
    color: ${colors.white};
  }
`;

const UniversityAttendanceNotice = styled.aside`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  margin-top: ${spacing[4]};
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.warning[300]};
  border-radius: ${borderRadius.md};
  background: ${colors.warning[50]};
  color: ${colors.warning[800]};
  font-size: ${typography.fontSizes.sm};
  line-height: 1.5;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

const MapEmbedContainer = styled.div`
  margin-top: ${spacing[4]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  background: ${colors.background.secondary};
`;

const MapIframe = styled.iframe`
  display: block;
  width: 100%;
  height: 320px;
  border: 0;
`;

const MapLinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
`;

const MapLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  background: ${colors.background.primary};
  color: ${colors.primary[700]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${colors.primary[50]};
    border-color: ${colors.primary[400]};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[12]};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.border.light};
  border-top: 3px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${spacing[12]};
  color: ${colors.text.secondary};
`;

const Toast = styled.div`
  position: fixed;
  left: 50%;
  bottom: ${spacing[8]};
  transform: translateX(-50%);
  z-index: 1700;
  padding: ${spacing[3]} ${spacing[5]};
  border-radius: ${borderRadius.full};
  background: ${colors.gray[800]};
  color: ${colors.white};
  font-size: ${typography.fontSizes.sm};
  box-shadow: ${shadows.lg};
`;

const BadgeArea = styled.div`
  position: relative;
  z-index: 1;
  margin-bottom: ${spacing[4]};
`;

export const EventDetailsPageComponent: React.FC = () => {
  const { eventId, eventSlug } = useParams<{ eventId: string; eventSlug?: string }>();
  const navigate = useNavigate();
  const { trackEvent, trackEventView, trackError } = useAnalytics();
  const { translations } = useTranslations();
  const [event, setEvent] = useState<EventInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [related, setRelated] = useState<RelatedEventsResponse | null>(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [nearbyEventCount, setNearbyEventCount] = useState<number | null>(null);
  const [eventsNearMeCenter, setEventsNearMeCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [locationEventsLink, setLocationEventsLink] = useState<{ path: string; city: string } | null>(null);
  const relatedImpressionRef = useRef<string | null>(null);

  // The numeric id is the identity; the slug is descriptive. Rewrite an
  // outdated or missing slug so bookmarks survive a title change.
  useEffect(() => {
    if (!event) return;
    // Navigating between two events reuses this component, so `event` still
    // holds the previous one until its fetch resolves. Canonicalising then
    // would redirect back to the event we just left.
    if (String(event.eventId) !== eventId) return;
    const canonicalPath = buildEventPath(event as unknown as Event);
    if (!canonicalPath) return;
    if (`/event/${eventId}${eventSlug ? `/${eventSlug}` : ''}` !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [event, eventId, eventSlug, navigate]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((message: string) => {
    setToast(current => current === message ? current : message);
  }, []);

  const handleAddToCalendar = useCallback(() => {
    if (!event) return;
    const registrationLink = event.eventLinks?.find(link => link.kind === 'registration');
    const eventPageLink = event.eventLinks?.find(link => link.kind === 'event_page');
    downloadEventIcs({
      ...event,
      registrationUrl: registrationLink?.url || event.registrationUrl,
      eventUrl: eventPageLink?.url || event.eventUrl,
    } as unknown as Event);
    trackEvent({ category: 'event_detail', action: 'calendar_download', custom_parameters: { action_location: 'header_actions', event_type: event.isVirtual ? 'virtual' : 'in_person' } });
    showToast('Calendar file downloaded');
  }, [event, showToast, trackEvent]);

  const handleShare = useCallback(async () => {
    if (!event) return;
    const outcome = await shareEvent(event as unknown as Event);
    trackEvent({ category: 'event_detail', action: 'share', custom_parameters: { action_location: 'header_actions', outcome } });
    if (outcome === 'shared') showToast('Event shared');
    else if (outcome === 'copied') showToast('Link copied to clipboard');
    else if (outcome === 'failed') showToast('Could not share this event');
  }, [event, showToast, trackEvent]);

  const handleCopy = useCallback(async () => {
    if (!event) return;
    const sources = event.eventSources?.map(source => source.source) ?? [];
    const selectedRegistration = event.eventLinks?.find(link => link.kind === 'registration')?.url;
    const selectedEventPage = event.eventLinks?.find(link => link.kind === 'event_page')?.url;
    const safeRegistration = selectedRegistration ||
      toAbsoluteUrl(event.registrationUrl, [event.eventUrl, ...sources]);
    const copied = await copyEventSummary({
      ...event,
      registrationUrl: safeRegistration ?? undefined,
      eventUrl: selectedEventPage || event.eventUrl,
    });
    trackEvent({ category: 'event_detail', action: 'copy_summary', custom_parameters: { action_location: 'header_actions', outcome: copied ? 'copied' : 'failed' } });
    showToast(copied ? 'Event summary copied' : 'Could not copy event summary');
  }, [event, showToast, trackEvent]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventId) {
        setError('Event ID not provided');
        setIsLoading(false);
        return;
      }

      // Reset per-event state, otherwise the previous event stays on screen
      // while the new one loads.
      setIsLoading(true);
      setError(null);

      try {
        const eventDetails = await EventFinderAPI.getEventInfo(parseInt(eventId, 10));
        setEvent(eventDetails);
        
        // Track event view
        trackEventView(eventId, eventDetails.title, {
          event_type: eventDetails.isVirtual ? 'virtual' : 'in_person',
          event_city: eventDetails.city,
          event_state: eventDetails.state,
          has_description: !!eventDetails.description,
          source_count: eventDetails.eventSources?.length || 0,
        });
      } catch (err) {
        const error = err as Error;
        setError('Failed to load event details');
        trackError(error, `event_details_fetch_${eventId}`);
        console.error('Error fetching event details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]); // Only re-fetch when eventId changes

  useEffect(() => {
    setNearbyEventCount(null);
    setEventsNearMeCenter(null);
    if (!eventId || !navigator.geolocation || !navigator.permissions?.query) return;

    let cancelled = false;
    const controller = new AbortController();

    navigator.permissions.query({ name: 'geolocation' })
      .then(permission => {
        if (cancelled || permission.state !== 'granted') return;
        navigator.geolocation.getCurrentPosition(position => {
          if (cancelled) return;
          const center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setEventsNearMeCenter(center);
          EventFinderAPI.getNearbyEventCount(center.lat, center.lng, {
            radiusKm: 15,
            days: 30,
            excludeEventId: Number(eventId),
            signal: controller.signal,
          }).then(result => {
            if (!cancelled) setNearbyEventCount(result.count);
          }).catch(() => {
            // The generic action remains available when enrichment fails.
          });
        }, () => {
          // Permission can change between the query and geolocation call.
        });
      })
      .catch(() => {
        // Unsupported permission queries leave the generic action available.
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventId]);

  // Loaded separately so a slow or failed related-events lookup never blocks
  // the event detail itself.
  useEffect(() => {
    if (!config.features.relatedEvents || !eventId) return;

    const controller = new AbortController();
    setRelatedLoading(true);
    setRelated(null);

    EventFinderAPI.getRelatedEvents(parseInt(eventId, 10), { signal: controller.signal })
      .then(setRelated)
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Error fetching related events:', err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setRelatedLoading(false);
      });

    return () => controller.abort();
  }, [eventId]);

  useEffect(() => {
    setLocationEventsLink(null);
    if (!event) return;
    const city = event.city?.trim();
    if (!city) return;

    const controller = new AbortController();
    EventFinderAPI.resolveLocation({
      city,
      state: event.state,
      country: event.country,
    }, controller.signal)
      .then(resolution => {
        const path = resolution?.location.canonicalUrl;
        if (!path) return;
        setLocationEventsLink({
          path,
          city: resolution.location.cityLocalized || resolution.location.city || city,
        });
      })
      .catch(error => {
        if ((error as Error)?.name !== 'AbortError') {
          console.error('Error resolving event location:', error);
        }
      });

    return () => controller.abort();
  }, [event]);

  // Impression counterpart to related_event_click, so click-through rate has a
  // denominator. Fires once per event, including when nothing was found.
  useEffect(() => {
    if (!related || !eventId) return;
    if (relatedImpressionRef.current === eventId) return;
    relatedImpressionRef.current = eventId;

    const similarCount = related.similar.length;
    const aroundCount = related.aroundTheSameTime.length;
    trackEvent({
      category: 'event_detail',
      action: 'related_events_shown',
      custom_parameters: {
        similar_count: similarCount,
        around_count: aroundCount,
        has_results: similarCount + aroundCount > 0,
        anchor_is_virtual: related.anchorIsVirtual,
      },
    });
  }, [related, eventId, trackEvent]);

  const handleRelatedClick = useCallback((item: RelatedEventItem, section: string, index: number) => {
    trackEvent({
      category: 'event_detail',
      action: 'related_event_click',
      custom_parameters: {
        action_location: section,
        target_event_id: item.eventId,
        list_position: index + 1,
        time_relation: relationKey(item),
      },
    });
  }, [trackEvent]);

  const handleRelatedNavigation = useCallback((targetSection: string) => {
    trackEvent({
      category: 'event_detail',
      action: 'related_events_navigation_click',
      custom_parameters: {
        action_location: 'detail_navigation',
        target_section: targetSection,
      },
    });
  }, [trackEvent]);

  const handleLocationEventsNavigation = useCallback(() => {
    if (!locationEventsLink) return;
    trackEvent({
      category: 'event_detail',
      action: 'location_events_click',
      custom_parameters: {
        action_location: 'detail_navigation',
        city: locationEventsLink.city,
        destination: locationEventsLink.path,
      },
    });
  }, [locationEventsLink, trackEvent]);

  const handleEventsNearMe = useCallback(() => {
    trackEvent({
      category: 'event_detail',
      action: 'events_near_me_click',
      value: nearbyEventCount ?? undefined,
      custom_parameters: {
        action_location: 'detail_navigation',
        count_available: nearbyEventCount != null,
      },
    });
    if (eventsNearMeCenter) {
      navigate('/map', {
        state: {
          eventsNearMeCenter,
          radiusKm: 15,
        },
      });
      return;
    }
    navigate('/map');
  }, [eventsNearMeCenter, navigate, nearbyEventCount, trackEvent]);

  if (isLoading) {
    return (
      <EventDetailsPage>
        <Container>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </Container>
      </EventDetailsPage>
    );
  }

  if (error || !event) {
    return (
      <EventDetailsPage>
        <Container>
          <ErrorContainer>
            <h2>Event Not Found</h2>
            <p>{error || 'The requested event could not be found.'}</p>
            <ActionButton onClick={() => navigate('/browse')}>
              Browse Events
            </ActionButton>
          </ErrorContainer>
        </Container>
      </EventDetailsPage>
    );
  }

  const formattedStart = formatEventLocalDateTime(event.startDate, {
    locale: 'en-US',
    dateStyle: 'full',
  });
  const formattedEnd = formatEventLocalDateTime(event.endDate, {
    locale: 'en-US',
    dateStyle: 'full',
  });
  const eventDuration = formatEventLocalDuration(event.startDate, event.endDate);
  const when = getEventDateTimeDisplay(event as unknown as Event);
  const canonicalPath = buildEventPath(event as unknown as Event) || `/event/${eventId}`;
  const sourceLinks = event.eventSources?.map((source) => source.source) ?? [];
  const registrationLink = event.eventLinks?.find(link => link.kind === 'registration');
  const eventPageLink = event.eventLinks?.find(link => link.kind === 'event_page');
  const registrationUrl = registrationLink?.url ||
    toAbsoluteUrl(event.registrationUrl, [event.eventUrl, ...sourceLinks]);
  const externalEventUrl = eventPageLink?.url || toAbsoluteUrl(event.eventUrl, sourceLinks);

  return (
    <EventDetailsPage>
      <Container>
        <SEO
          title={`${event.title} | Event Finder`}
          description={event.description ? event.description.substring(0, 160) : `${event.title} - ${event.isVirtual ? 'Virtual' : getLocationLabel(event) || 'In-Person'} event on ${formattedStart}`}
          keywords={[event.title, event.city, event.state, event.isVirtual ? 'virtual event' : 'in-person event', ...(event.topics || [])].filter(Boolean).join(', ')}
          canonical={canonicalUrl(canonicalPath)}
          type="event"
          structuredData={[
            createEventStructuredData({
              title: event.title,
              description: event.description,
              startDate: event.startDate,
              endDate: event.endDate,
              isVirtual: event.isVirtual,
              eventUrl: externalEventUrl ?? undefined,
              venueName: typeof event.venue === 'object' ? event.venue?.name : event.venue,
              city: event.city,
              state: event.state,
              country: event.country,
              organizerName: event.involvement?.primaryHost?.displayName ||
                event.involvement?.primaryHost?.entityName,
              organizerUrl: event.involvement?.primaryHost?.canonicalUrl
                ? canonicalUrl(event.involvement.primaryHost.canonicalUrl)
                : undefined,
              price: event.price,
              registrationUrl: registrationUrl ?? undefined,
              imageUrl: undefined,
            }),
            createBreadcrumbStructuredData([
              { name: 'Home', url: 'https://www.eventfinder.site/' },
              { name: 'Browse Events', url: 'https://www.eventfinder.site/browse' },
              { name: event.title, url: canonicalUrl(canonicalPath) }
            ])
          ]}
        />
        <NavigationRow aria-label="Event navigation">
          <NavigationButton onClick={() => navigate(-1)}>
            <ArrowLeft aria-hidden="true" size={16} />
            Back
          </NavigationButton>
          <NavigationButton onClick={handleEventsNearMe}>
            <MapPin aria-hidden="true" size={16} />
            {nearbyEventCount == null
              ? 'Events Near Me'
              : `${nearbyEventCount} Event${nearbyEventCount === 1 ? '' : 's'} Near Me`}
          </NavigationButton>
          {(related?.aroundTheSameTime.length ?? 0) > 0 && (
            <NavigationAnchor
              href={`#${AROUND_THE_SAME_TIME_SECTION_ID}`}
              onClick={() => handleRelatedNavigation('around_the_same_time')}
            >
              <Clock aria-hidden="true" size={16} />
              Around the same time events
            </NavigationAnchor>
          )}
          {(related?.similar.length ?? 0) > 0 && (
            <NavigationAnchor
              href={`#${SIMILAR_EVENTS_SECTION_ID}`}
              onClick={() => handleRelatedNavigation('similar_events')}
            >
              <Tag aria-hidden="true" size={16} />
              Similar events
            </NavigationAnchor>
          )}
          {locationEventsLink && (
            <NavigationLink to={locationEventsLink.path} onClick={handleLocationEventsNavigation}>
              <MapPin aria-hidden="true" size={16} />
              Other events in {locationEventsLink.city}
            </NavigationLink>
          )}
        </NavigationRow>

        <EventHeader>
          <EventTitle>{event.title}</EventTitle>
          <BadgeArea>
            <BadgeRow>
              <EventPrimaryHostBadge
                host={event.involvement?.primaryHost}
                onClick={() => trackEvent({
                  category: 'event_detail',
                  action: 'primary_host_click',
                  custom_parameters: {
                    host_source: event.involvement?.primaryHostSource || 'unknown',
                    host_type: event.involvement?.primaryHost?.entityType || 'unknown',
                    is_high_link: event.involvement?.isHighLinkEvent === true,
                  },
                })}
              />
              {event.universityAttendanceAdvisory &&
                event.involvement?.primaryHost?.entityType?.toLowerCase() !== 'university' && (
                <UniversityHostedBadge
                  event={event as unknown as Event}
                  hosts={event.universityAttendanceAdvisory.universities}
                />
              )}
              <FormatBadge event={event as unknown as Event} />
              <TimeOfDayBadge event={event as unknown as Event} />
              <VibeBadges event={event as unknown as Event} />
            </BadgeRow>
          </BadgeArea>
          <EventMeta>
            <MetaItem>
              <Calendar size={20} />
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <time dateTime={when.startIso}>
                  {when.weekday ? `${when.weekday}, ${when.dateLabel}` : when.dateLabel || formattedStart}
                </time>
                {when.timeLabel && (
                  <span style={{ fontSize: typography.fontSizes.sm, opacity: 0.85 }}>{when.timeLabel}</span>
                )}
              </span>
            </MetaItem>
            {!event.isVirtual && (getVenueName(event.venue) || getLocationLabel(event)) && (
              <MetaItem style={{ alignItems: 'flex-start' }}>
                <MapPin size={20} />
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <strong>{getVenueName(event.venue) || getLocationLabel(event)}</strong>
                  {getVenueName(event.venue) && getLocationLabel(event) && (
                    <span style={{ fontSize: typography.fontSizes.sm, opacity: 0.85 }}>
                      {getLocationLabel(event)}
                    </span>
                  )}
                </span>
              </MetaItem>
            )}
            {event.isVirtual && (
              <MetaItem>
                <Users size={20} />
                Virtual Event
              </MetaItem>
            )}
            {eventDuration && (
              <MetaItem>
                <Clock size={20} />
                {eventDuration}
              </MetaItem>
            )}
          </EventMeta>
          <EventActions>
            <RememberButton eventId={event.eventId} actionLocation="event_detail_header" />
            {registrationUrl && (
              <ActionButton onClick={() => {
                trackEvent({ category: 'event_detail', action: 'registration_click', custom_parameters: { action_location: 'header_actions', event_type: event.isVirtual ? 'virtual' : 'in_person', source: registrationLink?.isOfficial ? 'official_registration' : 'selected_registration' } });
                window.open(registrationUrl, '_blank', 'noopener,noreferrer');
              }}>
                <ExternalLink size={16} />
                Register
              </ActionButton>
            )}
            <ActionButton className="secondary" onClick={handleAddToCalendar}>
              <CalendarPlus size={16} />
              Add to Calendar
            </ActionButton>
            <ActionButton className="secondary" onClick={handleShare}>
              <Share2 size={16} />
              Share
            </ActionButton>
            {config.features.enhancedEventDetail && (
              <ActionButton className="secondary" onClick={handleCopy}>
                <Copy size={16} />
                Copy
              </ActionButton>
            )}
            {externalEventUrl && (
              <ActionButton
                className="secondary"
                onClick={() => {
                  trackEvent({ category: 'event_detail', action: 'source_click', custom_parameters: { action_location: 'header_actions', source: 'canonical_event_page' } });
                  window.open(externalEventUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <ExternalLink size={16} />
                {eventPageLink?.label || 'Event page'}
              </ActionButton>
            )}
          </EventActions>
        </EventHeader>

        <EventContent>
          <MainContent>
            {event.description && (
              <Section>
                <SectionTitle>About This Event</SectionTitle>
                <Description>{event.description}</Description>
                {event.descriptionProvenance?.kind === 'synthesized' && (
                  <SummaryAttribution href="#event-sources">
                    Summary based on {event.descriptionProvenance.sourceCount} sources
                  </SummaryAttribution>
                )}
                {event.agendaSummary && (
                  <Description style={{ marginTop: spacing[4] }}>
                    <strong>Agenda:</strong> {event.agendaSummary}
                  </Description>
                )}
              </Section>
            )}

            <Section>
              <SectionTitle>Event Details</SectionTitle>
              <DetailsList>
                <DetailTerm>
                  <Calendar size={16} />
                  Start Date
                </DetailTerm>
                <DetailDefinition>{formattedStart}</DetailDefinition>

                <DetailTerm>
                  <Calendar size={16} />
                  End Date
                </DetailTerm>
                <DetailDefinition>{formattedEnd}</DetailDefinition>

                {eventDuration && (
                  <>
                    <DetailTerm>
                      <Clock size={16} />
                      Duration
                    </DetailTerm>
                    <DetailDefinition>{eventDuration}</DetailDefinition>
                  </>
                )}

                {!event.isVirtual && getVenueName(event.venue) && (
                  <>
                    <DetailTerm>
                      <MapPin size={16} />
                      Venue
                    </DetailTerm>
                    <DetailDefinition>
                      {getVenueName(event.venue)}
                      {hasVenueCoordinates(event.venue) ? (
                        <>
                          {getVenueFormattedAddress(event.venue) && (
                            <Subtle>{getVenueFormattedAddress(event.venue)}</Subtle>
                          )}
                          {!getVenueFormattedAddress(event.venue) &&
                            getVenueStreetAddress(event.venue) && (
                              <Subtle>{getVenueStreetAddress(event.venue)}</Subtle>
                            )}
                          {(() => {
                            const coords = getEventCoordinates(event);
                            if (!coords) return null;
                            const label = encodeURIComponent(
                              getVenueName(event.venue) ||
                                getLocationLabel(event) ||
                                'Event location',
                            );
                            const href = `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}&query_place_id=${label}`;
                            return (
                              <Subtle>
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: colors.primary[600], textDecoration: 'none' }}
                                >
                                  <ExternalLink
                                    size={12}
                                    style={{ verticalAlign: 'middle', marginRight: 4 }}
                                  />
                                  View on map
                                </a>
                              </Subtle>
                            );
                          })()}
                        </>
                      ) : (
                        <Subtle title={`Geocode status: ${getVenueGeocodeStatus(event.venue) || 'pending'}`}>
                          <AlertCircle
                            size={12}
                            style={{ verticalAlign: 'middle', marginRight: 4 }}
                          />
                          {getVenueAddressNotice(event.venue)}
                        </Subtle>
                      )}
                    </DetailDefinition>
                  </>
                )}

                {!event.isVirtual &&
                  getLocationLabel(event) &&
                  !isLocationRedundantWithVenue(event) && (
                  <>
                    <DetailTerm>
                      <MapPin size={16} />
                      Location
                    </DetailTerm>
                    <DetailDefinition>
                      {event.location?.formattedAddress || getLocationLabel(event)}
                      {event.location?.geocodeStatus &&
                        event.location.geocodeStatus !== 'success' &&
                        !event.location.inferredFrom && (
                          <Subtle title={`Geocode status: ${event.location.geocodeStatus}`}>
                            <AlertCircle
                              size={12}
                              style={{ verticalAlign: 'middle', marginRight: 4 }}
                            />
                            Map coordinates not yet available
                          </Subtle>
                        )}
                      {(() => {
                        const coords = getEventCoordinates(event);
                        return coords ? (
                          <Subtle>
                            {coords.latitude.toFixed(4)}°, {coords.longitude.toFixed(4)}°
                            {' — '}
                            {coords.inferredFrom === 'university-primary-location'
                              ? translations.events.universityCampusPrecision
                              : getCoordinatePrecisionLabel(coords)}
                          </Subtle>
                        ) : null;
                      })()}
                    </DetailDefinition>
                  </>
                )}

                {typeof event.distanceKm === 'number' && (
                  <>
                    <DetailTerm>
                      <Globe size={16} />
                      Distance
                    </DetailTerm>
                    <DetailDefinition>{event.distanceKm.toFixed(1)} km away</DetailDefinition>
                  </>
                )}

                <DetailTerm>
                  <Tag size={16} />
                  Event Type
                </DetailTerm>
                <DetailDefinition>
                  {event.isVirtual ? 'Virtual' : 'In-Person'}
                </DetailDefinition>

                {formatPrice(event.price, event.currency, event.isFree) && (
                  <>
                    <DetailTerm>
                      <DollarSign size={16} />
                      Price
                    </DetailTerm>
                    <DetailDefinition>
                      {formatPrice(event.price, event.currency, event.isFree)}
                    </DetailDefinition>
                  </>
                )}

                {formatCapacity(event.capacity, event.ticketsRemaining) && (
                  <>
                    <DetailTerm>
                      <Ticket size={16} />
                      Capacity
                    </DetailTerm>
                    <DetailDefinition>
                      {formatCapacity(event.capacity, event.ticketsRemaining)}
                    </DetailDefinition>
                  </>
                )}

                {formatAudienceLevel(event.audienceLevel) && (
                  <>
                    <DetailTerm>
                      <UserCheck size={16} />
                      Audience
                    </DetailTerm>
                    <DetailDefinition>{formatAudienceLevel(event.audienceLevel)}</DetailDefinition>
                  </>
                )}

                {formatDeadline(event.registrationDeadline) && (
                  <>
                    <DetailTerm>
                      <Clock size={16} />
                      Register by
                    </DetailTerm>
                    <DetailDefinition>{formatDeadline(event.registrationDeadline)}</DetailDefinition>
                  </>
                )}

                {event.prerequisites && (
                  <>
                    <DetailTerm>
                      <AlertCircle size={16} />
                      Prerequisites
                    </DetailTerm>
                    <DetailDefinition>{event.prerequisites}</DetailDefinition>
                  </>
                )}
              </DetailsList>

              {!event.isVirtual && (() => {
                const coords = getEventCoordinates(event);
                if (!coords) return null;
                const { latitude: lat, longitude: lng } = coords;
                // OpenStreetMap embed: small bbox around the point + marker pin.
                const delta = 0.01;
                const bbox = [
                  lng - delta,
                  lat - delta,
                  lng + delta,
                  lat + delta,
                ].join(',');
                const osmEmbed = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
                const label = encodeURIComponent(
                  getVenueName(event.venue) || getLocationLabel(event) || 'Event location',
                );
                const googleHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
                const bingHref = `https://www.bing.com/maps?cp=${lat}~${lng}&lvl=15&sp=point.${lat}_${lng}_${label}`;
                return (
                  <MapEmbedContainer>
                    <MapIframe
                      title="Event location map"
                      src={osmEmbed}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <div style={{ padding: spacing[3] }}>
                      <MapLinkRow>
                        <MapLinkButton
                          href={googleHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent({ category: 'event_detail', action: 'map_exit', custom_parameters: { action_location: 'venue_map', provider: 'google' } })}
                        >
                          <ExternalLink size={14} />
                          Open in Google Maps
                        </MapLinkButton>
                        <MapLinkButton
                          href={bingHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent({ category: 'event_detail', action: 'map_exit', custom_parameters: { action_location: 'venue_map', provider: 'bing' } })}
                        >
                          <ExternalLink size={14} />
                          Open in Bing Maps
                        </MapLinkButton>
                      </MapLinkRow>
                    </div>
                  </MapEmbedContainer>
                );
              })()}

              {event.universityAttendanceAdvisory && (() => {
                const names = event.universityAttendanceAdvisory.universities
                  .map(host => host.displayName || host.entityName)
                  .filter(Boolean)
                  .join(', ');
                const hostName = names || translations.events.universityFallbackName;
                return (
                  <UniversityAttendanceNotice
                    aria-label="University event attendance notice"
                    role="note"
                  >
                    <AlertCircle size={18} aria-hidden="true" />
                    <span>{translations.events.universityAttendanceNotice.replace(
                      '{university}',
                      hostName,
                    )}</span>
                  </UniversityAttendanceNotice>
                );
              })()}

              {registrationUrl && (
                <div style={{ marginTop: spacing[4] }}>
                  <RegisterButton
                    href={registrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent({ category: 'event_detail', action: 'registration_click', custom_parameters: { action_location: 'details_body', event_type: event.isVirtual ? 'virtual' : 'in_person', source: 'canonical_registration' } })}
                  >
                    <ExternalLink size={16} />
                    Register for this event
                  </RegisterButton>
                </div>
              )}
            </Section>

            {(cleanStringArray(event.topics).length > 0 ||
              cleanStringArray(event.industries).length > 0 ||
              cleanStringArray(event.targetRoles).length > 0) && (
              <Section>
                <SectionTitle>Topics &amp; audience</SectionTitle>
                {cleanStringArray(event.topics).length > 0 && (
                  <div style={{ marginBottom: spacing[4] }}>
                    <DetailTerm style={{ marginBottom: spacing[2] }}>
                      <Tag size={16} />
                      Topics
                    </DetailTerm>
                    <TagList>
                      {cleanStringArray(event.topics).map((t) => (
                        <TagPill key={`topic-${t}`} $tone="topic">{t}</TagPill>
                      ))}
                    </TagList>
                  </div>
                )}
                {cleanStringArray(event.industries).length > 0 && (
                  <div style={{ marginBottom: spacing[4] }}>
                    <DetailTerm style={{ marginBottom: spacing[2] }}>
                      <Tag size={16} />
                      Industries
                    </DetailTerm>
                    <TagList>
                      {cleanStringArray(event.industries).map((i) => (
                        <TagPill key={`ind-${i}`} $tone="industry">{i}</TagPill>
                      ))}
                    </TagList>
                  </div>
                )}
                {cleanStringArray(event.targetRoles).length > 0 && (
                  <div>
                    <DetailTerm style={{ marginBottom: spacing[2] }}>
                      <UserCheck size={16} />
                      Target roles
                    </DetailTerm>
                    <TagList>
                      {cleanStringArray(event.targetRoles).map((r) => (
                        <TagPill key={`role-${r}`} $tone="role">{r}</TagPill>
                      ))}
                    </TagList>
                  </div>
                )}
              </Section>
            )}

            {Array.isArray(event.speakers) && event.speakers.length > 0 && (
              <Section>
                <SectionTitle>Speakers</SectionTitle>
                {event.speakers.map((speaker, idx) => {
                  const label = formatSpeaker(speaker);
                  if (!label) return null;
                  return (
                    <SpeakerCard key={`speaker-${idx}`}>
                      <SpeakerName>
                        {speaker.name}
                        {speaker.keynote && <KeynoteBadge>Keynote</KeynoteBadge>}
                      </SpeakerName>
                      {(speaker.title || speaker.company) && (
                        <SpeakerMeta>
                          {[speaker.title, speaker.company].filter(Boolean).join(', ')}
                        </SpeakerMeta>
                      )}
                    </SpeakerCard>
                  );
                })}
              </Section>
            )}

            {config.features.relatedEvents && (
              <>
                <RelatedEventsSection
                  id={AROUND_THE_SAME_TIME_SECTION_ID}
                  title="Around the same time"
                  items={related?.aroundTheSameTime ?? []}
                  isLoading={relatedLoading}
                  onSelect={(item, index) => handleRelatedClick(item, 'around_the_same_time', index)}
                />
                <RelatedEventsSection
                  id={SIMILAR_EVENTS_SECTION_ID}
                  title="Similar events"
                  items={related?.similar ?? []}
                  isLoading={relatedLoading}
                  onSelect={(item, index) => handleRelatedClick(item, 'similar_events', index)}
                />
              </>
            )}
          </MainContent>

          <Sidebar>
            <EventInvolvementSection
              involvement={event.involvement}
              onToggle={expanded => trackEvent({
                category: 'event_detail',
                action: 'organizations_toggle',
                value: event.involvement?.organizationCount,
                custom_parameters: {
                  expanded,
                  is_high_link: event.involvement?.isHighLinkEvent === true,
                },
              })}
            />
            <EventSourcesDisclosure
              sources={event.eventSources}
              firstExtractedAt={event.firstExtractedAt}
              lastRefreshedAt={event.lastRefreshedAt}
              onToggle={open => {
                if (!open) return;
                trackEvent({
                  category: 'event_detail',
                  action: 'sources_open',
                  value: event.eventSources?.length || 0,
                });
              }}
            />
          </Sidebar>
        </EventContent>
      </Container>
      {toast && <Toast role="status">{toast}</Toast>}
    </EventDetailsPage>
  );
};

export default EventDetailsPageComponent;
